// zielbild_timeline — the result cards under the phase axis (v15 slide 4), in the master's
// exact geometry: six card positions (card 2 the gold-highlighted sheet, card 6 the dark
// terminal card) under the axis rule at y226; phases as 10px dots with a mono month and the
// phase name in Geist Black (phase colour); the last phase ends the axis with the ▶. Cards
// carry title, body (**bold** emphasis), and a foot rule with DEL refs + date; the terminal
// card holds up to two gold-bar milestones.
import type { ZielbildTimelineContent } from '../../../../shared/deckTypes.js';
import { Abs, Brand, Dkp, Emph, Eyebrow, PageNo, Rule } from './chrome.js';

interface CardGeom {
  x: number; w: number; dark: boolean; fill: string;
  bar: string; barH: number; titlePx: number; inX: number; inW: number; refColor: string;
}
const CARDS: readonly CardGeom[] = [
  { x: 64, w: 180, dark: false, fill: '#FFFFFF', bar: '#7FADA9', barH: 2.3, titlePx: 16, inX: 80, inW: 148, refColor: '#3F7B78' },
  { x: 264, w: 180, dark: false, fill: '#FCFBF6', bar: '#C8A96E', barH: 4.8, titlePx: 14.7, inX: 283.2, inW: 144, refColor: '#8A6F3E' },
  { x: 458, w: 160, dark: false, fill: '#FFFFFF', bar: '#C8A96E', barH: 2.3, titlePx: 14.7, inX: 474, inW: 128, refColor: '#8A6F3E' },
  { x: 638, w: 160, dark: false, fill: '#FFFFFF', bar: '#2E5F5C', barH: 2.3, titlePx: 14.7, inX: 654, inW: 128, refColor: '#2E5F5C' },
  { x: 812, w: 160, dark: false, fill: '#FFFFFF', bar: '#2E5F5C', barH: 2.3, titlePx: 14.7, inX: 828, inW: 128, refColor: '#2E5F5C' },
  { x: 992, w: 224, dark: true, fill: '#0B0C0E', bar: '#F3F1EC', barH: 2.3, titlePx: 15.5, inX: 1008, inW: 188, refColor: '#F3F1EC' },
];
const PHASE_DOT = ['#7FADA9', '#C8A96E', '#2E5F5C', '#121316'] as const;
const PHASE_TXT = ['#3F7B78', '#8A6F3E', '#2E5F5C', '#121316'] as const;

function phaseSpans(n: number): [number, number][] {
  switch (n) {
    case 1: return [[0, 5]];
    case 2: return [[0, 2], [3, 5]];
    case 3: return [[0, 1], [2, 3], [4, 5]];
    default: return [[0, 0], [1, 2], [3, 4], [5, 5]];
  }
}

export default function ZielbildTimelinePreview({ content, pageNo }: {
  content: ZielbildTimelineContent;
  pageNo?: number | null;
}) {
  const c = content;
  const phases = (c.phases ?? []).slice(0, 4);
  const spans = phaseSpans(Math.max(1, phases.length));
  const items = (c.items ?? []).slice(0, 6);
  const milestones = (c.terminal_milestones ?? []).slice(0, 2);

  return (
    <Dkp>
      <Eyebrow text={c.eyebrow ?? ''} />
      <Abs x={64} y={84} w={1000}><div className="dkp-title">{c.title}</div></Abs>
      {c.intro ? (
        <Abs x={64} y={130} w={900}>
          <div style={{ fontSize: 13.3, fontWeight: 500, color: '#3A3D42', lineHeight: 1.4 }}>
            <Emph text={c.intro} boldColor="#121316" />
          </div>
        </Abs>
      ) : null}

      <Rule x={64} y={226} w={968} h={1.3} />
      {phases.map((ph, i) => {
        const [a, b] = spans[i];
        const x0 = CARDS[a].x;
        const x1 = CARDS[b].x + CARDS[b].w;
        const k = Math.min(i, 3);
        return (
          <div key={i}>
            <Abs x={x0} y={221.5} w={10} h={10} style={{ background: PHASE_DOT[k], borderRadius: '50%' }} />
            {ph.month ? (
              <Abs x={x0} y={178} w={x1 - x0 - 8} style={{ textAlign: 'right' }}>
                <span className="dkp-mono" style={{ fontSize: 9.3, letterSpacing: '.08em', textTransform: 'uppercase', color: '#6B6E75' }}>{ph.month}</span>
              </Abs>
            ) : null}
            {ph.label ? (
              <Abs x={x0 + 16} y={196} w={x1 - x0 - 24}>
                <span style={{ fontSize: 12, fontWeight: 900, color: PHASE_TXT[k], lineHeight: 1.15, display: 'block' }}>{ph.label}</span>
              </Abs>
            ) : null}
            {i === phases.length - 1 && phases.length > 1 ? (
              <Abs x={x0 + 34} y={220} w={13} h={13} style={{
                width: 0, height: 0, borderTop: '6.5px solid transparent', borderBottom: '6.5px solid transparent',
                borderLeft: `13px solid ${PHASE_DOT[k]}`,
              }} />
            ) : null}
          </div>
        );
      })}

      {items.map((it, i) => {
        const g = CARDS[i];
        const fg = g.dark ? '#F3F1EC' : '#121316';
        return (
          <div key={i}>
            <Abs x={g.x} y={252} w={g.w} h={386} style={{ background: g.fill }} />
            <Abs x={g.x} y={252} w={g.w} h={g.barH} style={{ background: g.bar }} />
            {it.label ? (
              <Abs x={g.inX} y={g.dark ? 268 : 266} w={g.inW}>
                <div className="dkp-serif" style={{ fontSize: g.titlePx, lineHeight: 1.12, color: fg }}>{it.label}</div>
              </Abs>
            ) : null}
            {it.body ? (
              <Abs x={g.inX} y={g.dark ? 336 : 326} w={g.inW} h={g.dark ? 190 : 252} style={{ overflow: 'hidden' }}>
                <div style={{ fontSize: g.dark ? 11.2 : 10.7, fontWeight: 500, color: g.dark ? '#D9D6CF' : '#3A3D42', lineHeight: 1.5 }}>
                  <Emph text={it.body} boldColor={g.dark ? '#F3F1EC' : '#3A3D42'} />
                </div>
              </Abs>
            ) : null}
            {g.dark ? milestones.map((m, mi) => {
              const y = 537.7 + mi * 24;
              return (
                <div key={mi}>
                  <Abs x={g.inX} y={y} w={4} h={11} style={{ background: '#C8A96E' }} />
                  <Abs x={g.inX + 14} y={y - 2} w={g.inW - 14}>
                    <span className="dkp-mono" style={{ fontSize: 9.3, color: '#F3F1EC', fontWeight: 700 }}>{m.label}&nbsp;&nbsp;</span>
                    <span className="dkp-mono" style={{ fontSize: 9.3, color: '#D9D6CF' }}>{m.date}</span>
                  </Abs>
                </div>
              );
            }) : null}
            <Rule x={g.inX} y={596} w={g.inW} h={0.8} color={g.dark ? '#3A3C41' : '#D9D6CF'} />
            {it.refs ? (
              <Abs x={g.inX + (g.dark ? 2 : 22)} y={608} w={70}>
                <span className="dkp-mono" style={{ fontSize: 9.3, color: g.refColor }}>{it.refs}</span>
              </Abs>
            ) : null}
            {it.date ? (
              <Abs x={g.inX + 2} y={608} w={g.inW - 4} style={{ textAlign: 'right' }}>
                <span className="dkp-mono" style={{ fontSize: g.dark ? 8.5 : 9.3, fontWeight: 700, color: fg }}>{it.date}</span>
              </Abs>
            ) : null}
          </div>
        );
      })}

      <Brand />
      <PageNo n={pageNo} />
    </Dkp>
  );
}
